import type { Salarie } from '@shared/types/Salarie';
import type { Bulletin } from '@shared/types/Bulletin';
import type { SaisieBulletin } from './SaisieBulletin';
import { calculerParts } from './PartsFiscales';
import { roundFCFA } from '@shared/utils/round';
import { uuid } from '@shared/utils/uuid';
import { periodeFromDate } from '@shared/utils/fmtDate';
import { calculerPrimeAnciennete } from './PrimeAnciennete';
import { defaultParametresFiscauxPaie, type ParametresFiscauxPaie } from './ParametresFiscauxPaie';
import { calculerPaieRubriques } from './CalculPaieRubriques';
import { CODES_RUBRIQUES, rubriquesDepuisSaisie } from './RubriquesPaie';
import { ENGINE_HASH } from '@shared/constants/EngineVersion';

function dateFinPeriode(periode: string): Date {
  const [y, m] = periode.split('-').map(Number);
  return new Date(y ?? 2026, m ?? 1, 0);
}

export interface CalculBulletinOptions {
  periode?: string;
  ignorer_exonerations?: boolean;
  /** Paramètres fiscaux (charges patronales) — déduction salariale = moteur CGI. */
  params?: ParametresFiscauxPaie;
  /**
   * Exonération art. 91 11° déjà consommée par les bulletins antérieurs du salarié sur la même année civile
   * (plafond annuel et global de 4 000 000 FCFA). Voir `cumulExoArt9111Anterieur`.
   */
  exoArt9111Anterieur?: number;
}

/**
 * Fonction principale du moteur de paie Gabon CGI 2026.
 *
 * La saisie est convertie en RUBRIQUES classifiées (codes du référentiel paie v2,
 * `rubriquesDepuisSaisie`), calculées par le cœur unique `calculerPaieRubriques` :
 * CNSS / CNAMGS / TCS (`calculerCotisations`, arrondi ROUND) et IRPP (`calculerIRPP`).
 * Charges patronales = mêmes bases, paramètres du tenant.
 */
export function calculerBulletin(
  salarie: Salarie,
  saisie: SaisieBulletin,
  options: CalculBulletinOptions = {},
): Bulletin {
  const periode = options.periode ?? periodeFromDate(new Date());
  const params = options.params ?? defaultParametresFiscauxPaie(periode);

  const brut_base = roundFCFA(saisie.salaire_base ?? salarie.salaire_base);
  const sursalaire = roundFCFA(saisie.sursalaire ?? salarie.sursalaire ?? 0);
  const prime = roundFCFA(saisie.prime ?? 0);
  const prime_extra = roundFCFA(saisie.prime_extra ?? 0);
  const prime_anciennete = calculerPrimeAnciennete({
    salaireBase: brut_base,
    dateEmbauche: salarie.date_embauche,
    saisie: saisie.prime_anciennete,
    dateRef: dateFinPeriode(periode),
  });
  const prime_logement = roundFCFA(saisie.prime_logement ?? 0);
  const prime_vehicule = roundFCFA(saisie.prime_vehicule ?? 0);
  const treizieme_mois = roundFCFA(saisie.treizieme_mois ?? 0);
  const hsup_total = roundFCFA(saisie.hsup_total ?? 0);
  const transport = roundFCFA(saisie.transport ?? 0);
  const vehicule = roundFCFA(saisie.vehicule ?? 0);
  const logement = roundFCFA(saisie.logement ?? 0);
  const fonction_ind = roundFCFA(saisie.fonction_ind ?? 0);
  const ind_vehicule_cnss = roundFCFA(saisie.ind_vehicule_cnss ?? 0);
  const ind_representation = roundFCFA(saisie.ind_representation ?? 0);
  const ind_blanchissage = roundFCFA(saisie.ind_blanchissage ?? 0);
  const ind_vehicule_nc = roundFCFA(saisie.ind_vehicule_nc ?? 0);
  const autres = roundFCFA(saisie.autres ?? 0);
  const avantage_nature = roundFCFA(saisie.avantage_nature ?? 0);
  const avance = roundFCFA(saisie.avance ?? 0);

  const saisieNorm: SaisieBulletin = {
    ...saisie,
    salaire_base: brut_base,
    sursalaire,
    prime,
    prime_extra,
    prime_logement,
    prime_vehicule,
    prime_anciennete,
    treizieme_mois,
    hsup_total,
    transport,
    vehicule,
    logement,
    fonction_ind,
    ind_vehicule_cnss,
    ind_representation,
    ind_blanchissage,
    ind_vehicule_nc,
    autres,
    avantage_nature,
  };

  const parts = calculerParts(salarie, periode);
  const rubriques = rubriquesDepuisSaisie(saisieNorm, {
    ...(options.ignorer_exonerations ? { ignorerExonerations: true } : {}),
  });
  const resultat = calculerPaieRubriques(rubriques, parts, params, {
    exoArt9111DejaUtilise: options.exoArt9111Anterieur ?? 0,
  });
  const cotPat = resultat.cotisations;

  const base_cnss = cotPat.base_cnss;
  const base_cnamgs = cotPat.base_cnamgs;
  const cnss_pf_emp = cotPat.cnss_pf_emp;
  const cnss_rp_emp = cotPat.cnss_rp_emp;
  const cnss_vid_emp = cotPat.cnss_vid_emp;
  const cnamgs_emp = cotPat.cnamgs_emp;
  const cnamgs_fam_emp = cotPat.cnamgs_fam_emp;
  const cfp_emp = cotPat.cfp_emp;
  const fnh_emp = cotPat.fnh_emp;
  const fgtls_emp = cotPat.fgtls_emp;
  const total_charges_emp = cotPat.total_charges_emp;

  /** Total versé (CAT1 + CAT2 + CAT3) ; brut imposable = « Total Brut » (CAT1 + CAT2). */
  const brut_total = resultat.brut_verse;
  const brut_imposable = resultat.brut_soumis;
  const exo_ss_total = resultat.cat3_total;
  const montantCode = (code: string): number =>
    roundFCFA(rubriques.filter((r) => r.code === code).reduce((acc, r) => acc + r.montant, 0));
  const transport_exonere = montantCode(CODES_RUBRIQUES.INDEMNITE_TRANSPORT);
  const transport_cnss = montantCode(CODES_RUBRIQUES.PRIME_TRANSPORT_CNSS);
  const vehicule_exonere = vehicule > 0 ? montantCode(CODES_RUBRIQUES.INDEMNITE_VEHICULE) : 0;
  const vehicule_cnss = vehicule > 0 ? montantCode(CODES_RUBRIQUES.INDEMNITE_VEHICULE_CNSS) : 0;

  /** Coût employeur = brut versé + charges patronales (≠ net + patronales). */
  const cout_employeur = roundFCFA(brut_total + total_charges_emp);

  /**
   * CSS (décision dirigeant en attente — valeur et seuil inchangés, paramètres nommés) :
   * retenue salariale appliquée après les autres retenues.
   */
  const css = cotPat.css;
  /**
   * Net à payer = salaire net + indemnités non imposables (CAT3, 34xxx) − avances (40050)
   * − avantage en nature non versé − CSS.
   */
  const net_a_payer = roundFCFA(resultat.net_avec_non_cotisable - avantage_nature - avance - css);

  const cat1_total = resultat.cat1_total;
  const cat2_total = resultat.cat2_total;
  const cat3_total = resultat.cat3_total;

  return {
    id: uuid('bul'),
    tenant_id: salarie.tenant_id,
    salarie_id: salarie.id,
    matricule: salarie.matricule,
    nom_complet: `${salarie.prenom} ${salarie.nom}`.trim(),
    periode,
    date_emission: new Date().toISOString(),

    brut_base,
    sursalaire,
    prime,
    prime_extra,
    prime_logement,
    prime_vehicule,
    prime_anciennete,
    treizieme_mois,
    hsup_total,
    transport,
    transport_cnss,
    transport_exonere,
    vehicule,
    vehicule_cnss,
    vehicule_exonere,
    logement,
    fonction_ind,
    ind_vehicule_cnss: vehicule > 0 ? vehicule_cnss : ind_vehicule_cnss,
    ind_representation,
    ind_blanchissage,
    ind_vehicule_nc: vehicule > 0 ? vehicule_exonere : ind_vehicule_nc,
    autres,
    avantage_nature,
    jours_travailles: saisie.jours_travailles,

    cat1_total,
    cat2_total,
    cat3_total,
    brut_total,
    brut_imposable,
    exo_ss_total,
    base_cnss,
    base_cnamgs,
    base_tcs: resultat.base_tcs,
    exo_art91_11: resultat.exo_art91_11,
    cnss_pf_emp,
    cnss_rp_emp,
    cnss_vid_emp,
    cnss_vid_sal: resultat.cnss,
    cnamgs_emp,
    cnamgs_fam_emp,
    cnamgs_sal: resultat.cnamgs,
    cfp_emp,
    fnh_emp,
    fgtls_emp,
    tcs: resultat.tcs,
    css,
    total_charges_emp,
    cout_employeur,

    parts,
    abattement: resultat.irpp.abattement,
    net_imposable: resultat.irpp.net_imposable,
    irpp_mensuel: resultat.irpp_mensuel,

    net_a_payer,
    avance,

    engine_hash: ENGINE_HASH,
    cree_le: new Date().toISOString(),
  };
}
